#!/usr/bin/env node

import { Command } from 'commander';
import { scoreCommand } from './commands/score.js';
import { initCommand } from './commands/init.js';
import { dnaCommand } from './commands/dna.js';
import { autoCommand } from './commands/auto.js';
import { loopCommand } from './commands/loop.js';
import { syncCommand } from './commands/sync.js';
import { compileCommand } from './commands/compile.js';
import { decompileCommand } from './commands/decompile.js';
import { checkCommand } from './commands/check.js';
import { exportCommand } from './commands/export.js';
import { serverCardCommand } from './commands/server-card.js';
import { cardsCommand } from './commands/cards.js';
import { showCommand } from './commands/show.js';
import { gitCommand } from './commands/git.js';
import { diffCommand, diffDriverCommand } from './commands/diff.js';
import { logCommand } from './commands/log.js';
import { hooksCommand, hooksRun } from './commands/hooks.js';
import { infoCommand } from './commands/info.js';
import { formatsCommand } from './commands/formats.js';
import { clearCommand } from './commands/clear.js';
import { convertCommand } from './commands/convert.js';
import { contextCommand } from './commands/context.js';
import { driftCommand } from './commands/drift.js';
import { editCommand } from './commands/edit.js';
import { recoverCommand } from './commands/recover.js';
import { migrateCommand } from './commands/migrate.js';
import { proCommand } from './commands/pro.js';
import { shareCommand } from './commands/share.js';
import { demoCommand } from './commands/demo.js';
import { searchCommand } from './commands/search.js';
import { tafCommand } from './commands/taf.js';
import { goCommand } from './commands/go.js';
import { aiCommand } from './commands/ai.js';
import { conductorCommand } from './commands/conductor.js';
import { wjttcCommand } from './commands/wjttc.js';
import { benchCommand } from './commands/bench.js';
import { refreshCommand } from './commands/refresh.js';
import { memoryCommand } from './commands/memory.js';
import { isOneLineError, oneLine } from './core/refusal.js';

const { version: VERSION } = require('../package.json');

const program = new Command();

program
  .name('faf')
  .description('Foundational AI context + memory — .faf and .fafm for any AI')
  .version(VERSION, '-v, --version');

// === Core Commands (Top 6) ===

program
  .command('init')
  .description('Create .faf from your project')
  .option('--yolo', 'Quick init with sensible defaults')
  .option('--quick', 'Alias for --yolo')
  .option('--force', 'Overwrite existing project.faf')
  .option('--output <path>', 'Output path')
  .action((options) => initCommand(options));

program
  .command('dna')
  .description('🧬 Show your FAF DNA journey (birth → growth)')
  .action(() => dnaCommand());

program
  .command('auto')
  .description('Fill every tech slot from the repo, then score')
  .action(() => autoCommand());

program
  .command('loop')
  .description('Drive to 100% — source what we can, then ask only the human')
  .option('-r, --rounds <n>', 'max auto rounds before stopping', '5')
  .action((options) => loopCommand(options));

program
  .command('go')
  .description('Guided interview to gold code')
  .option('--resume', 'Resume previous session')
  .action((options) => goCommand(options));

program
  .command('score [file]')
  .description('Score a .faf file')
  .option('--verbose', 'Show slot breakdown')
  .option('--status', 'Compact one-liner output')
  .option('--json', 'Output as JSON')
  .action((file, options) => scoreCommand(file, options));

program
  .command('bench [action] [answersFile]')
  .description('AI-grounding benchmark — cold vs with-faf (the .faf is the answer key)')
  .option('--json', 'Output as JSON')
  .option('--cold', 'Grade as the without-context run')
  .option('--faf', 'Grade as the with-context run')
  .option('--tokens <n>', 'Tokens used in the run (reported by the agent)')
  .option('--model <name>', 'Model that answered (reported)')
  .option('--file <path>', 'Path to project.faf (default: discovered)')
  .option('--submit', 'Submit the full-pair receipt to the public bench ledger')
  .option('--endpoint <url>', 'Override the submit endpoint')
  .action((action, answersFile, options) => benchCommand(action, answersFile, options));

program
  .command('show')
  .description('Render project.faf → project.html and open it')
  .option('--force', 'Replace a project.html faf cannot prove it rendered (edited since, or not faf\'s)')
  .action((options) => showCommand(options));

program
  .command('sync')
  .description('.faf → CLAUDE.md (--direction pull: Trophy-gated backfill into .faf)')
  .option('--watch', 'Watch for changes')
  .option('--direction <dir>', 'auto|push write CLAUDE.md from .faf; pull backfills .faf at ✪ Trophy', 'auto')
  .action((options) => syncCommand(options));

// === Power Commands ===

program
  .command('compile [file]')
  .description('Compile .faf to .fafb binary')
  .option('--output <path>', 'Output path')
  .option('--force', 'Replace an output file that is not a .fafb faf compiled')
  .action((file, options) => compileCommand(file, options));

program
  .command('decompile <file>')
  .description('Decompile .fafb to JSON')
  .option('--output <path>', 'Output path')
  .option('--force', 'With --output: replace a file faf cannot prove it wrote (edited since, or not a faf decompile)')
  .action((file, options) => decompileCommand(file, options));

program
  .command('git <url>')
  .description('Instant scored context from any GitHub repo (any branch/tag with --ref)')
  .option('--ref <ref>', 'Clone at a specific branch or tag (versioned context)')
  .option('--output <path>', 'Write to a custom path (default: ./project.faf)')
  .option('--force', 'Overwrite an existing project.faf')
  .option('--stdout', 'Print the .faf to stdout instead of writing a file')
  .action((url, options) => gitCommand(url, options));

program
  .command('diff [range]')
  .description('Semantic context diff between two .faf versions (slot deltas + score). git ranges: <ref>, A..B, A...B')
  .option('--json', 'Emit the delta as structured JSON')
  .option('--install-driver', 'Wire faf diff into native git — `git diff` renders .faf score+slot deltas')
  .option('--uninstall-driver', 'Remove the native-git diff driver')
  .action((range, options) => diffCommand(range, options));

program
  .command('diff-driver [args...]', { hidden: true })
  .description('Internal: GIT_EXTERNAL_DIFF handler invoked by git for diff=faf files')
  .action((args) => diffDriverCommand(args ?? []));

program
  .command('log')
  .description('Score timeline across git history — the .faf score at every commit that touched it')
  .option('-n, --limit <n>', 'Max commits to show (default 20)')
  .option('--all', 'Show every commit, no cap')
  .option('--reverse', 'Oldest-first instead of newest-first')
  .option('--json', 'Emit the timeline as structured JSON')
  .action((options) => logCommand(options));

program
  .command('hooks')
  .description('Pre-commit context guard — scores staged .faf vs HEAD, warns (or --strict blocks) on regression')
  .option('--install', 'Install the pre-commit hook')
  .option('--uninstall', 'Remove the faf hook block (preserves other hooks)')
  .option('--strict', 'With --install: block the commit on a score regression (default: warn only)')
  .action((options) => hooksCommand(options));

program
  .command('hooks-run', { hidden: true })
  .description('Internal: the pre-commit context guard the git hook invokes')
  .option('--strict', 'Exit non-zero on a score regression')
  .action((options) => hooksRun(options));

program
  .command('export')
  .description('Export context files from .faf')
  .option('--agents', 'Author AGENTS.md')
  .option('--cursor', 'Author .cursorrules')
  .option('--gemini', 'Author GEMINI.md')
  .option('--copilot', 'Author .github/copilot-instructions.md (GitHub Copilot)')
  .option('--grok', 'Wire grok-faf-mcp into .grok/config.toml')
  .option('--llms', 'Author llms.txt (llmstxt.org view of authored 6Ws — opt-in)')
  .option('--conductor', 'Author conductor config')
  .option('--html', 'Author project.html (visual render of project.faf)')
  .option('--card', 'Author MCP Server Card (./server-card) with the FAF context-block')
  .option('--all', 'Author all formats')
  .option('--output <path>', 'Write exported files to this directory instead of the current one')
  .option('--force', 'Replace a project.html or Server Card faf cannot prove it wrote (edited since, or not faf\'s)')
  .action((options) => exportCommand(options));

program
  .command('server-card')
  .description('Emit the registry server.json identity (name + title + _meta) from project.faf — the cross-language server-card emitter; patches an existing file')
  .option('--in <path>', 'Existing server.json to patch (default: ./server.json)')
  .option('--out <path>', 'Output path (default: same as --in)')
  .option('--faf <path>', 'project.faf path (default: auto-discover)')
  .option('--set-version <version>', 'Set the version field (default: preserve existing). NOTE: not --version, which is the global CLI-version flag')
  .option('--generated <iso>', 'Override the _meta generated stamp (default: preserve existing)')
  .option('--check', 'Print to stdout, do not write (diff/verify — the idempotency-test hook)')
  .option('--force', 'With --out: replace a file faf cannot prove it wrote (edited since, or no faf identity)')
  .action((options) => serverCardCommand(options));

program
  .command('cards')
  .description('Project .faf + .fafa onto A2A / MCP / registry / catalog cards — one context block, every door')
  .option('--target <list>', 'Comma list: a2a,mcp,registry,catalog,ard (default: whatever inputs allow)')
  .option('--faf <path>', 'project.faf path (default: auto-discover)')
  .option('--fafa <path>', 'agent.fafa path (default: auto-discover)')
  .option('--dir <path>', 'Working directory (default: cwd)')
  .option('--a2a-url <url>', 'Public URL of the A2A card (catalog row)')
  .option('--door-url <url>', 'A2A door when .fafa has no a2a endpoint')
  .option('--faf-pointer <url>', 'Absolute .faf pointer for served cards (default: ./project.faf)')
  .option('--check', 'Print projected cards to stdout, do not write')
  .option('--force', 'Replace a card file faf cannot prove it wrote (edited since, or not faf\'s)')
  .action((options) => cardsCommand(options));

program
  .command('check [file]')
  .description('Validate .faf file')
  .option('--strict', 'Require 100% score')
  .option('--fix', 'Auto-fix issues')
  .option('--doctor', 'Full diagnostic')
  .option('--trust', 'Verify trust chain')
  .option('--verbose', 'Show slot breakdown')
  .action((file, options) => checkCommand(file, options));

program
  .command('info')
  .description('Show version and system info')
  .option('--version', 'Show version')
  .option('--faq', 'Show FAQ')
  .option('--index', 'Show command index')
  .option('--stacks', 'Show supported stacks')
  .action((options) => infoCommand(options));

// === Phase A Commands ===

program
  .command('formats')
  .description('Show supported formats')
  .action(() => formatsCommand());

program
  .command('clear')
  .description('Clear cached data')
  .action(() => clearCommand());

program
  .command('convert')
  .description('Convert .faf to other formats')
  .option('--json', 'Output as JSON')
  .action((options) => convertCommand(options));

program
  .command('context')
  .description('Author context output')
  .action(() => contextCommand());

program
  .command('refresh')
  .description('Re-ground on the live .faf — re-score, measure drift vs the DNA baseline, keep .fafb current')
  .option('--json', 'Output as JSON')
  .action((options) => refreshCommand(options));

program
  .command('drift')
  .description('Check context drift')
  .option('--json', 'Emit the drift report as structured JSON')
  .action((options) => driftCommand(options));

program
  .command('edit <path> <value>')
  .description('Edit .faf fields')
  .action((path, value) => editCommand(path, value));

// === Phase B Commands ===

program
  .command('recover')
  .description('Recover .faf from context files')
  .action(() => recoverCommand());

program
  .command('migrate')
  .description('Migrate .faf to latest version')
  .option('--dry-run', 'Preview changes without writing')
  .action((options) => migrateCommand(options));

program
  .command('pro [subcommand]')
  .description('Pro features & licensing')
  .action((subcommand) => proCommand(subcommand));

program
  .command('share')
  .description('Share .faf via URL')
  .option('--copy', 'Copy to clipboard')
  .option('--raw', 'Output encoded string only')
  .action((options) => shareCommand(options));

program
  .command('demo')
  .description('Demo walkthrough')
  .action(() => demoCommand());

program
  .command('search <query>')
  .description('Search slots and formats')
  .option('--slots', 'Search slots only')
  .option('--formats', 'Search formats only')
  .action((query, options) => searchCommand(query, options));

program
  .command('taf [subcommand]')
  .description('TAF Receipts — `faf taf setup` wires the CI receipt printer; bare snapshot deprecated → faf score --json')
  .option('--output <path>', 'Write score snapshot to file')
  .option('--write', 'taf setup: create .github/workflows/taf.yml')
  .option('--force', 'With --output: replace a file faf cannot prove it wrote (edited since, or not a TAF snapshot)')
  .action((subcommand, options) => tafCommand(subcommand, options));

// === Phase C Commands ===

program
  .command('ai [subcommand]')
  .description('Ask Claude for suggestions about project.faf (analyze)')
  .action((subcommand) => aiCommand(subcommand));

program
  .command('conductor [subcommand] [path]')
  .description('Conductor integration')
  .action((subcommand, path) => conductorCommand(subcommand, path));

program
  .command('wjttc')
  .description('Audit test suite for WJTTC tier coverage (vendor-neutral)')
  .option('--path <path>', 'Test directory (default: tests)')
  .option('--strict', 'Exit non-zero if any tests are untiered')
  .option('--json', 'Output as JSON for CI consumption')
  .action((options) => wjttcCommand(options));

program
  .command('memory [subcommand] [arg]')
  .description('.fafm soul ops — convert Claude memory, etch, recall, ls, show')
  .option('-f, --file <path>', 'Soul file (default: ./soul.fafm)')
  .option('-o, --output <path>', 'Output path (convert)')
  .option('--namepoint <handle>', 'Namepoint for new/converted soul')
  .option('--id <id>', 'Fact id (etch)')
  .option('--type <type>', 'Fact type filter / etch type')
  .option('--tag <tags>', 'Comma-separated tags (recall)')
  .option('--priority <level>', 'Min priority floor (recall) or etch priority')
  .option('--limit <n>', 'Max recall hits')
  .option('--json', 'JSON output')
  .option('--force', 'Overwrite an existing soul.fafm (convert)')
  .action((subcommand, arg, options) => memoryCommand(subcommand, arg, options));

// === Soft Deprecation Aliases (v5.x compat) ===

program.command('bi-sync', { hidden: true }).action(() => syncCommand());
program.command('status', { hidden: true }).action(() => scoreCommand(undefined, { status: true }));
program.command('agents', { hidden: true }).action(() => exportCommand({ agents: true }));
program.command('cursor', { hidden: true }).action(() => exportCommand({ cursor: true }));
program.command('gemini', { hidden: true }).action(() => exportCommand({ gemini: true }));
program.command('grok', { hidden: true }).action(() => exportCommand({ grok: true }));
program.command('llms', { hidden: true }).action(() => exportCommand({ llms: true }));
program.command('validate', { hidden: true }).action((file: string) => checkCommand(file));
program.command('yolo', { hidden: true }).action(() => initCommand({ yolo: true }));

// === Parse and run ===

// No command given → show Nelly header + score + help
if (process.argv.length <= 2) {
  // Wrap top-level await in an async IIFE so `bun build --compile` accepts it.
  // (Plain `bun build` tolerates top-level await; --compile is stricter.)
  (async () => {
    const { bold, dim, fafCyan } = await import('./ui/colors.js');
    const cwd = process.cwd().replace(process.env.HOME ?? '', '~');

    // Nelly — pixel-art elephant (10×6 grid, half-block rendering)
    const G = '\x1b[38;2;150;150;150m';   // gray fg
    const GB = '\x1b[48;2;150;150;150m';  // gray bg
    const DF = '\x1b[38;2;29;29;29m';     // dark fg (▄ trick)
    const DB = '\x1b[48;2;29;29;29m';     // dark bg
    const RS = '\x1b[0m';
    console.log('');
    // Row 1: back arc — canonical (March 23, commit 7eb1042)
    // Row 2: body — cell 3 is a SOLID DARK block (DF█ on GB bg) = the eye.
    // The canonical's `${DB}${G}▀` head-curve at cell 3 already put the eye
    // there as a subtle dark-bottom-half. Replacing with a full dark cell
    // makes the eye visible in any rendering (monochrome too) without changing
    // the silhouette width — `█` and `█` are the same character, just
    // re-coloured.
    console.log(`${DB} ${G}▄${GB}███████${DB}${G}▄${RS}`);
    console.log(`${DB} ${GB}${G}█${DF}█${G}███████${RS}  ${fafCyan(bold('faf'))} ${dim(`v${VERSION}`)}`);
    const GR = '\x1b[38;2;39;174;96m';    // grass green
    // Restore Nelly's clean dark gaps between legs (pre-grass-between-feet).
    // The fa74012 fix ("restore Nelly's eye — revert grass between feet") was
    // accidentally re-regressed somewhere between v6.0.12 and v6.3.3 (likely the
    // IIFE wrap copied an older line). Pinning the canonical here.
    console.log(`${DB}${G}▀${GB}${DF}▀${DB} ${GB}${G}██${DB}  ${GB}${G}██${DB} ${RS}  ${dim('Nelly Never Forgets')}`);
    console.log(`${GR}▔▔▔▔▔▔▔▔▔▔▔▔${RS}`);
    console.log(`${dim(`  ${  cwd}`)}`);
    console.log('');
    try { await scoreCommand(undefined, { status: true }); } catch {}
    console.log('');
    console.log(`  ${dim('Run')} ${fafCyan('faf --help')} ${dim('for commands')}`);
  })();
} else {
  // Cohesion: present the 7.0 git-native surface as one group in `faf --help`,
  // so diff / log / hooks / driver / git read as a unit — "feels like git".
  program.addHelpText(
    'after',
    [
      '',
      'Git-native (The GIT Version):',
      '  faf diff [range]            semantic context diff + score delta (A..B, A...B)',
      '  faf diff --install-driver   make native `git diff` render .faf deltas',
      '  faf log                     score timeline across history (Proof-Over-Time)',
      '  faf hooks --install         pre-commit guard against context regression',
      '  faf git <url> [--ref]       instant scored context from any repo, any branch/tag',
      '',
      '  "FAF is to Context what Git is to Versions."',
      '',
    ].join('\n'),
  );
  // A refused path (a link out of the project, a dangling link), a file that
  // is not UTF-8, one that changed on disk while faf was writing, one faf
  // cannot prove it wrote, or a write that failed with the original kept (a
  // read-only file, a full disk) is an answer, not a crash: say it in one line
  // and exit 1. parseAsync, so a refusal inside an async command (faf go,
  // faf ai) is caught too.
  program.parseAsync(process.argv).catch((e: unknown) => {
    if (!isOneLineError(e)) {throw e;}
    console.error(oneLine(e, commandHasForce()));
    process.exit(1);
  });
}

/** True when the command being run takes --force. */
function commandHasForce(): boolean {
  const name = process.argv[2];
  const cmd = program.commands.find(c => c.name() === name || c.aliases().includes(name));
  return cmd?.options.some(o => o.long === '--force') ?? false;
}
